import authService from "../services/authService";
import {
  clearActiveMasterAccountId,
  persistActiveMasterAccountId,
  resolveActiveMasterAccountId,
} from "./activeMasterAccount";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_ADMIN_API_URL ||
  "https://admin-staging-api-v2.krosskast.com"
).replace(/\/$/, "");

async function fetchMembershipDefaultMasterAccountId(token) {
  const response = await fetch(`${API_BASE_URL}/me/memberships`, {
    method: "GET",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) return null;
  const json = await response.json().catch(() => ({}));
  const data = json?.data || json;
  const id = data?.defaultMasterAccountId;
  return id ? String(id) : null;
}

async function resolveMasterAccountIdForBootstrap(token) {
  let masterAccountId = resolveActiveMasterAccountId(token);
  if (masterAccountId) return masterAccountId;

  masterAccountId = await fetchMembershipDefaultMasterAccountId(token);
  if (masterAccountId) {
    persistActiveMasterAccountId(masterAccountId, token);
  }
  return masterAccountId;
}

/**
 * Canonical tenant bootstrap: GET /me/tenant
 * @see docs/architecture/TENANT-KIND-AND-PROVISIONING.md (admin-api)
 */
export async function fetchMeTenant() {
  const token = await authService.getValidAccessToken();
  if (!token) {
    const err = new Error("Authentication required for tenant bootstrap");
    err.code = "AUTH_REQUIRED";
    throw err;
  }

  let masterAccountId = await resolveMasterAccountIdForBootstrap(token);
  if (!masterAccountId) {
    const err = new Error(
      "No active Master Account. Tenant bootstrap cannot proceed.",
    );
    err.code = "MISSING_ACTIVE_MA";
    throw err;
  }

  const callBootstrap = async (maId) => {
    const response = await fetch(`${API_BASE_URL}/me/tenant`, {
      method: "GET",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-KK-Active-Master-Account-Id": maId,
      },
    });
    return response;
  };

  let response = await callBootstrap(masterAccountId);

  // Stale/cross-user active MA → clear, rediscover from memberships, retry once.
  if (response.status === 403 || response.status === 401) {
    clearActiveMasterAccountId();
    const recovered = await fetchMembershipDefaultMasterAccountId(token);
    if (recovered && recovered !== masterAccountId) {
      persistActiveMasterAccountId(recovered, token);
      masterAccountId = recovered;
      response = await callBootstrap(masterAccountId);
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(
      body.message || `Tenant bootstrap failed (${response.status})`,
    );
    err.code = "BOOTSTRAP_HTTP";
    err.status = response.status;
    err.requestedMasterAccountId = masterAccountId;
    throw err;
  }

  const json = await response.json();
  const data = json?.data || json;
  if (!data?.masterAccountId || !data?.kind) {
    const err = new Error("Tenant bootstrap response missing required fields");
    err.code = "BOOTSTRAP_INVALID";
    throw err;
  }

  if (String(data.masterAccountId) !== String(masterAccountId)) {
    const err = new Error(
      "Active Master Account does not match authenticated tenant scope",
    );
    err.code = "TENANT_MISMATCH";
    err.requestedMasterAccountId = masterAccountId;
    err.responseMasterAccountId = data.masterAccountId;
    throw err;
  }

  persistActiveMasterAccountId(data.masterAccountId, token);
  return data;
}
